import { FC, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Stack, Theme } from '@mui/material';
import { DtFilter, DtFilterValue } from '../helpers';
import SegmentFieldSearch from './segment-field/search';
import SegmentedFieldMenu from './segmented-field-menu';
import { getFieldValue, hasFieldValues } from './helpers';
import { SavedFilterItem } from '@/lib/saved-filters';
import ViewSelector from './saved-filters/view-selector';
import MoreFiltersMenu from './more-filters-menu';
import Button from '@/components/button';

interface Props {
  filter: DtFilter;
  values: DtFilterValue[];
  onFilterChange: (values: DtFilterValue[]) => void;
  keyword?: string;
  onKeywordChange?: (value: string) => void;
  searchPlaceholder?: string;
  savedFilters?: SavedFilterItem[];
  activeSavedFilterId?: string;
  onApplySavedFilter?: (id: string) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  filterHeader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    width: '100%',
  },
  filtersRow: {
    overflowX: 'visible',
    '& > *': {
      minWidth: 0,
      maxWidth: '100%',
    },
  },
  searchSegment: {
    minWidth: 400,
  },
}));

const FilterHeader: FC<Props> = ({
  filter,
  values,
  onFilterChange,
  keyword = '',
  onKeywordChange,
  searchPlaceholder = 'Search',
  savedFilters = [],
  activeSavedFilterId = '',
  onApplySavedFilter,
}) => {
  const styles = useStyles();
  const { fields } = filter;

  const setValueForColumn = useCallback(
    (columnName: string, value: unknown) => {
      const rest = values.filter((val) => val.columnName !== columnName);
      if (value === 'all') {
        onFilterChange(rest);
        return;
      }
      onFilterChange([...rest, { columnName, value }]);
    },
    [values, onFilterChange]
  );

  const setColumnToDefault = useCallback(
    (columnName: string) => {
      setValueForColumn(columnName, 'all');
    },
    [setValueForColumn]
  );

  const hasSavedFilters = !!savedFilters.length;
  const hasActiveFilters = !!values.length;
  const hasKeywordSearch = !!onKeywordChange;
  const hasKeywordValue = !!keyword.trim().length;
  const hasAnyActiveState = hasActiveFilters || hasKeywordValue;

  const isFieldActive = useCallback(
    (field: DtFilter['fields'][number]) => {
      const { columnName, control = 'select', options = [] } = field;
      const currentValue = getFieldValue(values, columnName);
      return hasFieldValues(control, currentValue, options);
    },
    [values]
  );

  const additionalFields = fields.filter((field) => field.additional);
  const visibleFields = fields.filter((field) => {
    if (!field.additional) {
      return true;
    }
    return isFieldActive(field);
  });

  const showMoreFilters = !!additionalFields.length;
  const showViewSelector = hasSavedFilters && onApplySavedFilter;

  const canClearAll = hasAnyActiveState && !!visibleFields.length;

  const handleClearFilters = () => {
    onFilterChange([]);
    onKeywordChange?.('');
  };

  return (
    <Stack
      className={styles.filterHeader}
      role="group"
      justifyContent="space-between"
      gap={1}
      minHeight={0}
      py={1}
    >
      {showViewSelector && (
        <Stack direction="row">
          <ViewSelector
            savedFilters={savedFilters}
            activeSavedFilterId={activeSavedFilterId}
            hasActiveFilters={hasAnyActiveState}
            onApplyFilter={onApplySavedFilter}
            onClearFilter={handleClearFilters}
          />
        </Stack>
      )}

      <Stack direction="row" alignItems="flex-start" gap={1} minWidth={0}>
        <Stack
          direction="row"
          className={styles.filtersRow}
          alignItems="center"
          gap={1}
          minWidth={0}
          flexGrow={1}
          flexWrap="wrap"
        >
          {hasKeywordSearch && (
            <SegmentFieldSearch
              className={styles.searchSegment}
              leftLabel="Search"
              leftIcon="search"
              leftActive={hasKeywordValue}
              name="filter-header-keyword"
              value={keyword}
              onChange={onKeywordChange}
              placeholder={searchPlaceholder}
            />
          )}
          {visibleFields.map((field) => {
            const { columnName } = field;
            return (
              <SegmentedFieldMenu
                key={columnName}
                field={field}
                values={values}
                setValueForColumn={setValueForColumn}
                setColumnToDefault={setColumnToDefault}
              />
            );
          })}
        </Stack>

        <Stack direction="row" alignItems="center" gap={2} flexShrink={0}>
          {showMoreFilters && (
            <MoreFiltersMenu
              fields={additionalFields}
              values={values}
              setValueForColumn={setValueForColumn}
              setColumnToDefault={setColumnToDefault}
            />
          )}
          <Button
            type="option"
            label="Clear All"
            disabled={!canClearAll}
            onClick={handleClearFilters}
            icon="close"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default FilterHeader;
